import { useEffect, useState } from "react";
import {
  Box, Stack, Typography, Button, Chip, Alert, CircularProgress, IconButton, TextField,
  MenuItem, Divider, Tooltip, Drawer, Link as MuiLink,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  listReconciliationRuns, getReconciliationDetail,
  ReconciliationRunSummary, ReconciliationDetail, RunStatus,
} from "../services/reconciliations";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function StatusChip({ status }: { status: RunStatus }) {
  const colors: Record<string, [string, string, string]> = {
    all_found: ["#166534", "#DCFCE7", "All found"],
    loaded:    ["#1E40AF", "#DBEAFE", "Cargado"],
    partial:   ["#9A6700", "#FFF6E0", "Partial"],
    error:     ["#B91C1C", "#FEE2E2", "Error"],
  };
  const [fg, bg, label] = colors[status] ?? ["#333", "#EEE", status];
  return (
    <Chip label={label} size="small"
      sx={{ fontWeight: 700, fontSize: 11, height: 26, borderRadius: 3, color: fg, bgcolor: bg }} />
  );
}

export default function RunsView() {
  const [rows,    setRows]    = useState<ReconciliationRunSummary[]>([]);
  const [cursor,  setCursor]  = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState<string | null>(null);
  const [accountFilter, setAccountFilter] = useState<"" | "45" | "84">("");
  const [dateFilter,    setDateFilter]    = useState<string>("");

  const [openDetailFor, setOpenDetailFor] = useState<string | null>(null);
  const [detail,        setDetail]        = useState<ReconciliationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function load(append = false) {
    setLoading(true); setErr(null);
    try {
      const r = await listReconciliationRuns({
        limit: 50,
        cursor: append ? cursor ?? undefined : undefined,
        account: accountFilter || undefined,
        date: dateFilter || undefined,
      });
      setRows(append ? [...rows, ...r.items] : r.items);
      setCursor(r.cursor);
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountFilter, dateFilter]);

  async function openDetail(runId: string) {
    setOpenDetailFor(runId); setDetail(null); setDetailLoading(true);
    try {
      setDetail(await getReconciliationDetail(runId));
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally {
      setDetailLoading(false);
    }
  }

  function closeDetail() { setOpenDetailFor(null); setDetail(null); }

  const hasMore = !!cursor;

  return (
    <>
      <Box sx={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25,
        mb: 2, p: 1.5, bgcolor: "#F7F6FC",
        border: "1px solid", borderColor: "rgba(91,47,235,0.12)", borderRadius: 3,
      }}>
        <TextField size="small" select fullWidth={false} label="Cuenta" value={accountFilter}
          onChange={(e) => setAccountFilter(e.target.value as any)} sx={{ width: 168 }}>
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="45">1-749049/45</MenuItem>
          <MenuItem value="84">1-749049/84</MenuItem>
        </TextField>
        <TextField size="small" type="date" fullWidth={false} label="Fecha" value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 158 }} />
        <Box sx={{ ml: "auto" }}>
          <Tooltip title="Refresh">
            <span><IconButton size="small" onClick={() => load(false)} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span>
          </Tooltip>
        </Box>
      </Box>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fecha / Hora</TableCell>
              <TableCell>Cuenta</TableCell>
              <TableCell align="right">Transferencias</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                Sin corridas registradas.
              </TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={`${r.run_id}#${r.account}`} hover>
                <TableCell>{fmtDate(r.started_at)}</TableCell>
                <TableCell>{r.account}</TableCell>
                <TableCell align="right">{r.total_ids_extracted}</TableCell>
                <TableCell><StatusChip status={r.status} /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Ver detalle">
                    <IconButton size="small" onClick={() => openDetail(r.run_id)}>
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {hasMore && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Button onClick={() => load(true)} disabled={loading} variant="outlined" size="small">
            {loading ? "Cargando…" : "Cargar más"}
          </Button>
        </Stack>
      )}

      <Drawer anchor="right" open={!!openDetailFor} onClose={closeDetail}
        PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, p: 2 } }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Detalle de la corrida</Typography>
          <IconButton onClick={closeDetail}><CloseIcon /></IconButton>
        </Stack>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 2 }}>
          run_id: {openDetailFor}
        </Typography>
        {detailLoading && <CircularProgress size={24} />}
        {!detailLoading && detail && (
          <Stack spacing={2} divider={<Divider />}>
            {detail.items.map((it) => (
              <Box key={it.account}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Cuenta {it.account}</Typography>
                  <StatusChip status={it.status} />
                  <Box sx={{ flex: 1 }} />
                  {it.xls_url && (
                    <MuiLink href={it.xls_url} target="_blank" rel="noopener" underline="hover">.xls original</MuiLink>
                  )}
                </Stack>
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                  product: {it.product || "(vacío)"} • {fmtDate(it.started_at)} → {fmtDate(it.finished_at)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Transferencias cargadas: <b>{it.total_ids_extracted}</b>
                </Typography>
                {it.error_message && <Alert severity="error" sx={{ mt: 1 }}>{it.error_message}</Alert>}
              </Box>
            ))}
          </Stack>
        )}
      </Drawer>
    </>
  );
}
